import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { works, type WorkType } from "@/mocks/works";
import FilterPanel, { type SortKey } from "./components/FilterPanel";

const PAGE_SIZE = 9;

export default function Works() {
  usePageMeta({
    title: "翔田千里影视作品列表－翔田千里影视演员作品全集介绍",
    description:
      "翔田千里影视作品全集，收录 2015 至 2024 年间的电影与电视剧作品，可按作品类型、上映年份、角色类型筛选，并按上映时间或热度排序，快速定位作品详情。",
    keywords: "翔田千里影视作品,翔田千里电影,翔田千里电视剧,作品列表,演员作品",
  });

  const [searchParams] = useSearchParams();
  const [type, setType] = useState<WorkType | "all">("all");
  const [year, setYear] = useState<number | "all">("all");
  const [role, setRole] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const typeParam = searchParams.get("type");
    setType(typeParam === "movie" || typeParam === "series" ? typeParam : "all");
    const roleParam = searchParams.get("role");
    setRole(roleParam && roleParam !== "all" ? roleParam : "all");
    const sortParam = searchParams.get("sort");
    setSort(sortParam === "popular" ? "popular" : "latest");
    setVisibleCount(PAGE_SIZE);
  }, [searchParams]);

  useEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  const filtered = useMemo(() => {
    const list = works.filter((work) => {
      if (type !== "all" && work.type !== type) return false;
      if (year !== "all" && work.year !== year) return false;
      if (role !== "all" && work.roleType !== role) return false;
      return true;
    });
    return list.sort((a, b) =>
      sort === "popular" ? b.popularity - a.popularity : b.releaseDate.localeCompare(a.releaseDate),
    );
  }, [type, year, role, sort]);

  const visibleWorks = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleWorks.length;

  const reset = () => {
    setType("all");
    setYear("all");
    setRole("all");
    setSort("latest");
    setVisibleCount(PAGE_SIZE);
  };

  const activeFilterCount =
    (type !== "all" ? 1 : 0) + (year !== "all" ? 1 : 0) + (role !== "all" ? 1 : 0);

  const updateFilter = (updater: () => void) => {
    updater();
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200/70 bg-background-50">
        <div className="container-page py-9 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视作品" }]} />
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-[40px]">
                翔田千里影视作品
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                收录翔田千里 2015 至 2024 年间的电影与电视剧作品，涵盖悬疑、剧情、家庭、爱情等类型。可按作品类型、上映年份与角色类型筛选，或按上映时间、热度排序。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-700">共 {works.length} 部作品</span>
              <span className="rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-900">
                电影 {works.filter((work) => work.type === "movie").length} 部
              </span>
              <span className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                电视剧 {works.filter((work) => work.type === "series").length} 部
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-8 md:py-10">
        <div className="container-page">
          <div className="hidden md:block">
            <FilterPanel
              type={type}
              year={year}
              role={role}
              sort={sort}
              onTypeChange={(value) => updateFilter(() => setType(value))}
              onYearChange={(value) => updateFilter(() => setYear(value))}
              onRoleChange={(value) => updateFilter(() => setRole(value))}
              onSortChange={(value) => updateFilter(() => setSort(value))}
              onReset={reset}
            />
          </div>

          <div className="flex items-center justify-between gap-3 md:hidden">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300/80 bg-background-50 px-4 py-2.5 text-sm text-foreground-800"
            >
              <i className="ri-filter-3-line" aria-hidden="true"></i>
              筛选与排序
              {activeFilterCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] text-primary-950">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <span className="text-xs text-foreground-500">共 {filtered.length} 部</span>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-foreground-500">
              当前显示 <span className="text-foreground-900">{filtered.length}</span> 部作品
              {hasMore && `，已加载 ${visibleWorks.length} 部`}
            </p>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={reset}
                className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-accent-700 hover:text-accent-800"
              >
                <i className="ri-close-circle-line" aria-hidden="true"></i>
                清除 {activeFilterCount} 个筛选条件
              </button>
            )}
          </div>

          {visibleWorks.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
              {visibleWorks.map((work, index) => (
                <Reveal key={work.id} delay={(index % 6) * 50}>
                  <WorkCard work={work} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                <i className="ri-film-line" aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg text-foreground-900">没有符合条件的作品</h2>
              <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                换个年份或角色类型试试，也可以直接重置筛选，查看全部作品。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选条件
              </button>
            </div>
          )}

          {hasMore && (
            <div className="mt-8 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 6)}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary-300 bg-background-50 px-6 py-3 text-sm text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-add-line" aria-hidden="true"></i>
                加载更多作品
              </button>
              <p className="text-[11px] text-foreground-400">
                还剩 {filtered.length - visibleWorks.length} 部未显示
              </p>
            </div>
          )}

          {!hasMore && visibleWorks.length > 0 && (
            <p className="mt-8 text-center text-[11px] text-foreground-400">已显示全部 {filtered.length} 部作品</p>
          )}

          <div className="mt-10 rounded-lg border border-background-200/80 bg-background-50 p-5 md:flex md:items-center md:justify-between md:gap-6">
            <div>
              <h2 className="font-heading text-base text-foreground-950">找不到想看的作品？</h2>
              <p className="mt-1.5 text-xs text-foreground-600">
                试试全站搜索，可同时检索作品名、类型标签与相关资讯标题。
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
              <Link
                to="/search"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
                全站搜索
              </Link>
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                演员资料
              </Link>
            </div>
          </div>
        </div>
      </section>

      {sheetOpen && (
        <div className="fixed inset-0 z-[70] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-primary-950/60"
          ></button>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-xl bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="关闭"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-lg text-foreground-700"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            </div>
            <FilterPanel
              type={type}
              year={year}
              role={role}
              sort={sort}
              onTypeChange={(value) => updateFilter(() => setType(value))}
              onYearChange={(value) => updateFilter(() => setYear(value))}
              onRoleChange={(value) => updateFilter(() => setRole(value))}
              onSortChange={(value) => updateFilter(() => setSort(value))}
              onReset={reset}
            />
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-primary-600 py-3 text-sm text-background-50"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}